import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

export const PERM_LABELS = { invoices: "Invoices", service_due: "Service due", stickers: "Stickers", routes: "Route setup", reminders: "Unpaid reminders", customers: "Customers", export: "Export / download", bulk_delete: "Delete many records" };
const PERM_DEFAULTS = { invoices: true, service_due: true, stickers: true, routes: true, reminders: true, customers: true, export: false, bulk_delete: false };

export default function TechDialog({ tech, databases, onClose, onSaved }) {
  const isEdit = !!tech?.id;
  const [v, setV] = useState({ name: "", email: "", password: "", db_ids: [], active: true, perms: PERM_DEFAULTS });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tech) setV({ name: tech.name || "", email: tech.email || "", password: "", db_ids: tech.db_ids || [], active: tech.active ?? true, perms: { ...PERM_DEFAULTS, ...tech.perms } });
  }, [tech]);

  const toggleDb = (id) => setV((s) => ({ ...s, db_ids: s.db_ids.includes(id) ? s.db_ids.filter((x) => x !== id) : [...s.db_ids, id] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...v, password: v.password || null };
      const { data } = isEdit ? await api.put(`/users/${tech.id}`, body) : await api.post("/users", body);
      toast.success(isEdit ? "Tech updated" : `${v.name} added`);
      onSaved(data, !isEdit);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tech} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto" data-testid="tech-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit tech" : "Invite a tech"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="tech-name">Name</Label>
            <Input id="tech-name" required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="tech-name-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="tech-email">Email (also used for Google sign-in)</Label>
            <Input id="tech-email" type="email" required value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} data-testid="tech-email-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="tech-password">{isEdit ? "New password (leave blank to keep)" : "Password (optional; leave blank and they'll set their own from the invite link)"}</Label>
            <Input id="tech-password" type="password" minLength={6} value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} data-testid="tech-password-input" /></div>
          <div className="space-y-2">
            <Label>Databases this tech can open</Label>
            <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto" data-testid="tech-db-checkboxes">
              {databases.length === 0 && <p className="text-xs text-muted-foreground">Upload a database first.</p>}
              {databases.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={v.db_ids.includes(d.id)} onCheckedChange={() => toggleDb(d.id)} data-testid={`tech-db-${d.id}`} />
                  {d.name} <span className="text-xs text-muted-foreground">({d.filename} · {d.tables.reduce((s, t) => s + t.row_count, 0)} records{d.description ? ` · ${d.description.slice(0, 40)}` : ""})</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>What this tech can use</Label>
            <div className="border rounded-md p-3 grid grid-cols-2 gap-2" data-testid="tech-perm-checkboxes">
              {Object.entries(PERM_LABELS).map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={!!v.perms[k]} onCheckedChange={(c) => setV({ ...v, perms: { ...v.perms, [k]: !!c } })} data-testid={`tech-perm-${k}`} /> {l}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={v.active} onCheckedChange={(c) => setV({ ...v, active: c })} data-testid="tech-active-switch" /> Can sign in
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="tech-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} className="hover:bg-blue-600 hover:text-white" data-testid="tech-save-button">
              {saving && <Loader2 size={14} className="animate-spin" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
